import React, { useState, useEffect, useCallback } from "react";
import { 
  Box, 
  Typography, 
  Chip, 
  Tooltip, 
  Button, 
  Input
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import TagTransactionInput from "./tagTransactionInput";
import { backendPatchWithoutRefresh } from "../../../helpers/callBackend";
import SelectTransactionCategories from "./selectTransactionCategories";
import LoadingButton from '@mui/lab/LoadingButton';
import SelectCleanCounterparty from "./SelectCleanCounterparty";
import GenericTagChips from "../../specific/customTags/genericTagChips";

// Improved EditableField component outside the main component to prevent re-creation
const EditableField = React.memo(({ 
	label, 
	value, 
	placeholder, 
	isEditable, 
	isLoading = false,
	maxLength = 50,
	onUpdate,
	onSave,
	onCancel,
	onChange,
	theme,
	fieldLabelStyles,
	fieldValueStyles,
	buttonStyles
}) => (
	<Box sx={{ marginBottom: '16px' }}>
		<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
			<Box sx={{ flex: 1 }}>
				<Typography sx={fieldLabelStyles}>
					{label}
				</Typography>
				{isEditable ? (
					<Input
						fullWidth
						value={value}
						onChange={onChange}
						inputProps={{ maxLength }}
						disabled={isLoading}
						autoFocus
						sx={{
							marginTop: '4px',
							fontSize: theme.typography.body1.fontSize,
							fontWeight: 500,
							color: theme.palette.text.primary,
							'&:before': {
								borderBottomColor: theme.palette.divider,
							},
							'&:hover:not(.Mui-disabled):before': {
								borderBottomColor: theme.palette.primary.main,
							},
							'&.Mui-focused:after': {
								borderBottomColor: theme.palette.primary.main,
							}
						}}
					/>
				) : (
					<Typography sx={fieldValueStyles}>
						{value || placeholder}
					</Typography>
				)}
			</Box>
			<Box sx={{ marginLeft: '12px' }}>
				{isEditable ? (
					<Box sx={{ display: 'flex', gap: '4px' }}>
						<LoadingButton 
							onClick={onSave} 
							loading={isLoading}
							sx={buttonStyles.primary}
							size="small"
						>
							Save
						</LoadingButton>
						<Button 
							onClick={onCancel} 
							sx={buttonStyles.secondary}
							disabled={isLoading}
						>
							Cancel
						</Button>
					</Box>
				) : (
					<LoadingButton 
						onClick={onUpdate} 
						sx={buttonStyles.secondary}
						size="small"
					>
						Update
					</LoadingButton>
				)}
			</Box>
		</Box>
	</Box>
));

function CompletedTransactionDrawer(props) {
	const theme = useTheme();
	const transactionId = props.selectedTransaction.id;
	const [counterpartyName, setCounterpartyName] = useState(props.selectedTransaction.counterparty_name);
	const [dateOfTransaction, setDateOfTransaction] = useState(
		props.selectedTransaction.timestamp
			? dayjs(props.selectedTransaction.timestamp)
			: null
	);
	
	// Existing state
	const [dateEditable, setDateEditable] = useState(false);
	const [counterpartyEditable, setCounterpartyEditable] = useState(false);
	const [notesEditable, setNotesEditable] = useState(false);
	const [notes, setNotes] = useState(props.selectedTransaction.notes || "");
	const [checkNoEditable, setCheckNoEditable] = useState(false);
	const [checkNo, setCheckNo] = useState(props.selectedTransaction.check_number || "");
	const [refNoEditable, setRefNoEditable] = useState(false);
	const [refNo, setRefNo] = useState(props.selectedTransaction.reference_number || "");
	
	// New fields state
	const [invoiceNoEditable, setInvoiceNoEditable] = useState(false);
	const [invoiceNo, setInvoiceNo] = useState(props.selectedTransaction.invoice_number || "");
	const [baiCodeEditable, setBaiCodeEditable] = useState(false);
	const [baiCode, setBaiCode] = useState(props.selectedTransaction.bai_code || "");
	
	// Loading states for all fields
	const [dateLoading, setDateLoading] = useState(false);
	const [counterpartyLoading, setCounterpartyLoading] = useState(false);
	const [notesLoading, setNotesLoading] = useState(false);
	const [checkNoLoading, setCheckNoLoading] = useState(false);
	const [refNoLoading, setRefNoLoading] = useState(false);
	const [invoiceNoLoading, setInvoiceNoLoading] = useState(false);
	const [baiCodeLoading, setBaiCodeLoading] = useState(false);
	const [glAccountLoading, setGlAccountLoading] = useState(false);
	const [inFlight, setInFlight] = useState(false);

	useEffect(() => {
		if (props.open) {
			setCounterpartyName(props.selectedTransaction.counterparty_name);
			setDateOfTransaction(
				props.selectedTransaction.timestamp
					? dayjs(props.selectedTransaction.timestamp)
					: null
			);
			setNotes(props.selectedTransaction.notes || "");
			setCheckNo(props.selectedTransaction.check_number || "");
			setRefNo(props.selectedTransaction.reference_number || "");
			setInvoiceNo(props.selectedTransaction.invoice_number || "");
			setBaiCode(props.selectedTransaction.bai_code || "");
		}
	}, [props.open, props.selectedTransaction]);

	// Common styles using theme
	const fieldLabelStyles = {
		fontSize: '12px',
		color: theme.palette.secondary?.[400] || theme.palette.text.secondary,
		textTransform: 'uppercase',
		fontWeight: 600,
		marginBottom: '4px'
	};

	const fieldValueStyles = {
		fontSize: theme.typography.body1.fontSize,
		fontWeight: 500,
		color: theme.palette.secondary?.dark || theme.palette.text.primary
	};

	const amountStyles = {
		fontSize: '18px',
		fontWeight: 600,
		color: theme.palette.primary.main
	};

	const buttonStyles = {
		primary: {
			padding: '4px 12px',
			fontSize: '11px',
			border: `1px solid ${theme.palette.primary.main}`,
			backgroundColor: theme.palette.primary.main,
			color: 'white',
			borderRadius: '4px',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: theme.palette.primary.dark,
			}
		},
		secondary: {
			padding: '4px 12px',
			fontSize: '11px',
			border: `1px solid ${theme.palette.divider}`,
			backgroundColor: 'white',
			color: theme.palette.text.secondary,
			borderRadius: '4px',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: theme.palette.action.hover,
			}
		}
	};

	const sectionBoxStyles = {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: '8px',
		padding: '20px',
		marginBottom: '20px'
	};

	// Generic field update handler
	const handleFieldUpdate = async (fieldName, fieldValue) => {
		try {
			let postData = {
				id: transactionId,
			};

			// Map frontend field names to backend field names and prepare data
			switch (fieldName) {
				case 'date':
					postData.date = fieldValue;
					break;
				case 'counterparty_name':
					postData.counterparty_name = fieldValue;
					break;
				case 'notes':
					postData.notes = fieldValue;
					break;
				case 'check_number':
					postData.check_number = fieldValue;
					break;
				case 'reference_number':
					postData.reference_number = fieldValue;
					break;
				case 'invoice_number':
					postData.invoice_number = fieldValue;
					break;
				case 'bai_code':
					postData.bai_code = fieldValue;
					break;
				case 'general_ledger_account':
					// Expecting fieldValue to be an object with { category, parentCategory }
					postData.category = fieldValue.category;
					postData.parentCategory = fieldValue.parentCategory;
					break;
				case 'custom_tags':
					// Expecting fieldValue to be an array of tag IDs
					postData.tag_ids = fieldValue;
					break;
				default:
					throw new Error(`Unknown field: ${fieldName}`);
			}

			const response = await backendPatchWithoutRefresh("/api/transactions/", postData);
			return response.data.data; // Returns the updated transaction
		} catch (error) {
			console.error(`Error updating ${fieldName}:`, error);
			throw error;
		}
	};

	const handleSelectCategories = async (selectedCategories = []) => {
		try {
			setInFlight(true);
			const updatedTransaction = await handleFieldUpdate('custom_tags', selectedCategories);
			props.handleCustomCategoriesChange(updatedTransaction);
		} catch (err) {
			console.error(err);
		} finally {
			setInFlight(false);
		}
	};

	// Date handler
	const handleDateChange = async (dateValue) => {
		try {
			setDateLoading(true);
			const updatedTransaction = await handleFieldUpdate('date', dateValue);
			if (props.onTransactionUpdate) {
				props.onTransactionUpdate(updatedTransaction);
			}
		} catch (error) {
			console.error('Failed to update date:', error);
			// Reset to original value on error
			setDateOfTransaction(
				props.selectedTransaction.timestamp
					? dayjs(props.selectedTransaction.timestamp)
					: null
			);
		} finally {
			setDateLoading(false);
		}
	};

	// Counterparty handler
	const handleCounterpartyNameChange = async (counterpartyName) => {
		try {
			setCounterpartyLoading(true);
			const updatedTransaction = await handleFieldUpdate('counterparty_name', counterpartyName);
			if (props.onTransactionUpdate) {
				props.onTransactionUpdate(updatedTransaction);
			}
		} catch (error) {
			console.error('Failed to update counterparty:', error);
			// Reset to original value on error
			setCounterpartyName(props.selectedTransaction.counterparty_name);
		} finally {
			setCounterpartyLoading(false);
		}
	};

	// Notes handler
	const handleNotesChange = async (notesValue) => {
		try {
			setNotesLoading(true);
			const updatedTransaction = await handleFieldUpdate('notes', notesValue);
			if (props.onTransactionUpdate) {
				props.onTransactionUpdate(updatedTransaction);
			}
		} catch (error) {
			console.error('Failed to update notes:', error);
			// Reset to original value on error
			setNotes(props.selectedTransaction.notes || "");
		} finally {
			setNotesLoading(false);
		}
	};

	// G/L Account handler
	const handleGLAccountChange = async (category, parentCategory) => {
		try {
			setGlAccountLoading(true);
			const updatedTransaction = await handleFieldUpdate('general_ledger_account', {
				category,
				parentCategory
			});
			if (props.onTransactionUpdate) {
				props.onTransactionUpdate(updatedTransaction);
			}
		} catch (error) {
			console.error('Failed to update G/L account:', error);
		} finally {
			setGlAccountLoading(false);
		}
	};

	const handleEdit = async (input) => {
		if (input === "date") {
			if (dateEditable) {
				await handleDateChange(dateOfTransaction ? dateOfTransaction.format("YYYY-MM-DD") : "");
			}
			setDateEditable(!dateEditable);
		} else if (input === "counterparty") {
			if (counterpartyEditable) {
				await handleCounterpartyNameChange(counterpartyName);
			}
			setCounterpartyEditable(!counterpartyEditable);
		}
	};

	const handleChange = (input) => (value) => {
		if (input === "dateOfTransaction") {
			setDateOfTransaction(value);
		} else if (input === "counterpartyName") {
			setCounterpartyName(value.target.value);
		}
	};

	const handleCancel = (input) => {
		if (input === "date") {
			setDateEditable(false);
			setDateOfTransaction(
				props.selectedTransaction.timestamp
					? dayjs(props.selectedTransaction.timestamp)
					: null
			);
		} else if (input === "counterparty") {
			setCounterpartyEditable(false);
			setCounterpartyName(props.selectedTransaction.counterparty_name);
		}
	};

	// Notes handlers
	const handleNotesEdit = async () => {
		if (notesEditable) {
			await handleNotesChange(notes);
		}
		setNotesEditable(!notesEditable);
	};

	const handleNotesCancel = () => {
		setNotesEditable(false);
		setNotes(props.selectedTransaction.notes || "");
	};

	const handleNotesInputChange = (event) => {
		setNotes(event.target.value);
	};

	// Fixed field handlers using createFieldHandlers with useCallback
	const createFieldHandlers = useCallback((fieldName, currentValue, stateSetter, editableSetter, loadingSetter) => ({
		handleUpdate: () => editableSetter(true),
		handleSave: async () => {
			try {
				loadingSetter(true);
				
				const updatedTransaction = await handleFieldUpdate(fieldName, currentValue);
				
				// Update local state with the returned value
				if (updatedTransaction[fieldName] !== undefined) {
					stateSetter(updatedTransaction[fieldName]);
				}
				editableSetter(false);
				
				if (props.onTransactionUpdate) {
					props.onTransactionUpdate(updatedTransaction);
				}
			} catch (error) {
				console.error(`Failed to update ${fieldName}:`, error);
				// Reset to original value on error
				stateSetter(props.selectedTransaction[fieldName] || "");
			} finally {
				loadingSetter(false);
			}
		},
		handleCancel: () => {
			editableSetter(false);
			stateSetter(props.selectedTransaction[fieldName] || "");
		},
		handleChange: (event) => stateSetter(event.target.value)
	}), [props.selectedTransaction, props.onTransactionUpdate]);

	// Update the handler creation calls to pass the current values:
	const checkNoHandlers = createFieldHandlers('check_number', checkNo, setCheckNo, setCheckNoEditable, setCheckNoLoading);
	const refNoHandlers = createFieldHandlers('reference_number', refNo, setRefNo, setRefNoEditable, setRefNoLoading);  
	const invoiceNoHandlers = createFieldHandlers('invoice_number', invoiceNo, setInvoiceNo, setInvoiceNoEditable, setInvoiceNoLoading);
	const baiCodeHandlers = createFieldHandlers('bai_code', baiCode, setBaiCode, setBaiCodeEditable, setBaiCodeLoading);



	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box sx={{ 
				fontFamily: theme.typography.fontFamily,
				width: '100%',
				minWidth: 0,
				maxWidth: '100%'
			}}>
				{/* Header */}
				<Box sx={{ 
					display: 'flex', 
					justifyContent: 'space-between', 
					alignItems: 'center',
					paddingBottom: '16px',
					borderBottom: `2px solid ${theme.palette.divider}`,
					marginBottom: '20px'
				}}>
					<Typography variant="h4" sx={{ 
						margin: 0, 
						fontWeight: 'normal',
						color: theme.palette.text.primary
					}}>
						{props.selectedTransaction.counterparty_name || ""}
					</Typography>
					<Typography variant="h4" sx={{ 
						margin: 0, 
						fontWeight: 'normal',
						color: theme.palette.primary.main
					}}>
						${Math.abs(props.selectedTransaction.amount || 0).toLocaleString('en-US', { 
							minimumFractionDigits: 2, 
							maximumFractionDigits: 2 
						})}
					</Typography>
				</Box>

				{/* Transaction Details Section */}
				<Box sx={sectionBoxStyles}>
					<Typography variant="h5" sx={{ 
						margin: '0 0 20px 0',
						color: theme.palette.text.primary
					}}>
						Transaction Details
					</Typography>

					<Box sx={{ display: 'flex', gap: '40px' }}>
						{/* Left Column */}
						<Box sx={{ flex: 1 }}>
							{/* Account Name */}
							<Box sx={{ marginBottom: '16px' }}>
								<Typography sx={fieldLabelStyles}>
									ACCOUNT NAME
								</Typography>
								<Typography sx={fieldValueStyles}>
									{props.selectedTransaction.info?.bank_account || ""}
								</Typography>
							</Box>

							{/* Method */}
							<Box sx={{ marginBottom: '16px' }}>
								<Typography sx={fieldLabelStyles}>
									METHOD
								</Typography>
								<Typography sx={fieldValueStyles}>
									{props.selectedTransaction.transaction_type || ""}
								</Typography>
							</Box>

							{/* Amount */}
							<Box sx={{ marginBottom: '16px' }}>
								<Typography sx={fieldLabelStyles}>
									AMOUNT
								</Typography>
								<Typography sx={amountStyles}>
									${Math.abs(props.selectedTransaction.amount || 0).toLocaleString('en-US', { 
										minimumFractionDigits: 2, 
										maximumFractionDigits: 2 
									})}
								</Typography>
							</Box>
						</Box>

						{/* Right Column */}
						<Box sx={{ flex: 1 }}>
							{/* Date */}
							<Box sx={{ marginBottom: '16px' }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
									<Box sx={{ flex: 1 }}>
										<Typography sx={fieldLabelStyles}>
											DATE
										</Typography>
										{dateEditable ? (
											<DatePicker
												value={dateOfTransaction}
												onChange={handleChange("dateOfTransaction")}
												disabled={dateLoading}
												slotProps={{
													textField: {
														variant: "standard",
														fullWidth: true,
														sx: {
															'& .MuiInput-root': {
																fontSize: theme.typography.body1.fontSize,
															}
														}
													}
												}}
											/>
										) : (
											<Typography sx={fieldValueStyles}>
												{props.selectedTransaction.timestamp ? 
													dayjs(props.selectedTransaction.timestamp).format("MMM DD, YYYY") : 
													""
												}
											</Typography>
										)}
									</Box>
									<Box sx={{ marginLeft: '12px' }}>
										{dateEditable ? (
											<Box sx={{ display: 'flex', gap: '4px' }}>
												<LoadingButton 
													onClick={() => handleEdit("date")} 
													loading={dateLoading}
													sx={buttonStyles.primary}
													size="small"
												>
													Save
												</LoadingButton>
												<Button 
													onClick={() => handleCancel("date")} 
													sx={buttonStyles.secondary}
													disabled={dateLoading}
												>
													Cancel
												</Button>
											</Box>
										) : (
											<LoadingButton 
												onClick={() => setDateEditable(true)} 
												sx={buttonStyles.secondary}
												size="small"
											>
												Update
											</LoadingButton>
										)}
									</Box>
								</Box>
							</Box>

							{/* Counterparty */}
							<Box sx={{ marginBottom: '16px' }}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
									<Box sx={{ flex: 1 }}>
										<Typography sx={fieldLabelStyles}>
											COUNTERPARTY
										</Typography>
										{counterpartyEditable ? (
											<SelectCleanCounterparty
												handleCounterpartyNameChange={handleCounterpartyNameChange}
												handleCounterpartyAssign={props.handleCounterpartyAssign}
												setCounterpartyEditable={setCounterpartyEditable}
												disabled={counterpartyLoading}
											/>
										) : (
											<Box>
												<Typography sx={{
													...fieldValueStyles,
													marginBottom: '4px'
												}}>
													{props.selectedTransaction.counterparty_name || ""}
												</Typography>
												{props.selectedTransaction?.counterparty?.entity && (
													<Tooltip arrow title={<Typography>This is a payment between accounts</Typography>}>
														<Chip 
															label="Internal"
															size="small"
															sx={{ 
																backgroundColor: theme.palette.action.hover,
																fontSize: '10px',
																height: '20px'
															}}
														/>
													</Tooltip>
												)}
											</Box>
										)}
									</Box>
									<Box sx={{ marginLeft: '12px' }}>
										{!counterpartyEditable && (
											<LoadingButton 
												onClick={() => setCounterpartyEditable(true)} 
												sx={buttonStyles.secondary}
												size="small"
											>
												Update
											</LoadingButton>
										)}
									</Box>
								</Box>
							</Box>

							{/* Balance */}
							<Box sx={{ marginBottom: '16px' }}>
								<Typography sx={fieldLabelStyles}>
									BALANCE
								</Typography>
								<Typography sx={amountStyles}>
									${parseFloat(props.selectedTransaction.balance || 0).toLocaleString('en-US', { 
										minimumFractionDigits: 2, 
										maximumFractionDigits: 2 
									})}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>

				{/* Metadata Section */}
				<Box sx={sectionBoxStyles}>
					<Typography variant="h5" sx={{ 
						margin: '0 0 20px 0',
						color: theme.palette.text.primary
					}}>
						Metadata
					</Typography>

					{/* Description */}
					<Box sx={{ marginBottom: '20px' }}>
						<Typography sx={fieldLabelStyles}>
							DESCRIPTION
						</Typography>
						<Typography sx={{
							...fieldValueStyles,
							paddingBottom: '8px',
							borderBottom: `1px solid ${theme.palette.divider}`
						}}>
							{props.selectedTransaction.description || ""}
						</Typography>
					</Box>

					{/* Notes */}
					<Box sx={{ marginBottom: '20px' }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
							<Box sx={{ flex: 1 }}>
								<Typography sx={fieldLabelStyles}>
									NOTES
								</Typography>
								{notesEditable ? (
									<Input
										fullWidth
										value={notes}
										onChange={handleNotesInputChange}
										placeholder="Add notes..."
										inputProps={{ maxLength: 50 }}
										disabled={notesLoading}
										autoFocus
										sx={{
											marginTop: '4px',
											fontSize: theme.typography.body1.fontSize,
											fontWeight: 500,
											color: theme.palette.text.primary,
											'&:before': {
												borderBottomColor: theme.palette.divider,
											},
											'&:hover:not(.Mui-disabled):before': {
												borderBottomColor: theme.palette.primary.main,
											},
											'&.Mui-focused:after': {
												borderBottomColor: theme.palette.primary.main,
											}
										}}
									/>
								) : (
									<Typography sx={{
										...fieldValueStyles,
										paddingBottom: '8px',
										borderBottom: `1px solid ${theme.palette.divider}`,
										minHeight: '24px'
									}}>
										{props.selectedTransaction.notes || ""}
									</Typography>
								)}
							</Box>
							<Box sx={{ marginLeft: '12px' }}>
								{notesEditable ? (
									<Box sx={{ display: 'flex', gap: '4px' }}>
										<LoadingButton 
											onClick={handleNotesEdit} 
											loading={notesLoading}
											sx={buttonStyles.primary}
											size="small"
										>
											Save
										</LoadingButton>
										<Button 
											onClick={handleNotesCancel} 
											sx={buttonStyles.secondary}
											disabled={notesLoading}
										>
											Cancel
										</Button>
									</Box>
								) : (
									<LoadingButton 
										onClick={handleNotesEdit} 
										sx={buttonStyles.secondary}
										size="small"
									>
										Update
									</LoadingButton>
								)}
							</Box>
						</Box>
					</Box>

					{/* Reference fields in two columns */}
					<Box sx={{ display: 'flex', gap: '40px', marginBottom: '20px' }}>
						{/* Left Column */}
						<Box sx={{ flex: 1 }}>
							<EditableField
								label="REFERENCE NO."
								value={refNo}
								isEditable={refNoEditable}
								isLoading={refNoLoading}
								onUpdate={refNoHandlers.handleUpdate}
								onSave={refNoHandlers.handleSave}
								onCancel={refNoHandlers.handleCancel}
								onChange={refNoHandlers.handleChange}
								theme={theme}
								fieldLabelStyles={fieldLabelStyles}
								fieldValueStyles={fieldValueStyles}
								buttonStyles={buttonStyles}
							/>

							<EditableField
								label="INVOICE NO."
								value={invoiceNo}
								isEditable={invoiceNoEditable}
								isLoading={invoiceNoLoading}
								onUpdate={invoiceNoHandlers.handleUpdate}
								onSave={invoiceNoHandlers.handleSave}
								onCancel={invoiceNoHandlers.handleCancel}
								onChange={invoiceNoHandlers.handleChange}
								theme={theme}
								fieldLabelStyles={fieldLabelStyles}
								fieldValueStyles={fieldValueStyles}
								buttonStyles={buttonStyles}
							/>
						</Box>

						{/* Right Column */}
						<Box sx={{ flex: 1 }}>
							<EditableField
								label="CHECK NO."
								value={checkNo}
								isEditable={checkNoEditable}
								isLoading={checkNoLoading}
								onUpdate={checkNoHandlers.handleUpdate}
								onSave={checkNoHandlers.handleSave}
								onCancel={checkNoHandlers.handleCancel}
								onChange={checkNoHandlers.handleChange}
								theme={theme}
								fieldLabelStyles={fieldLabelStyles}
								fieldValueStyles={fieldValueStyles}
								buttonStyles={buttonStyles}
							/>

							<EditableField
								label="BAI CODE"
								value={baiCode}
								isEditable={baiCodeEditable}
								isLoading={baiCodeLoading}
								maxLength={3}
								onUpdate={baiCodeHandlers.handleUpdate}
								onSave={baiCodeHandlers.handleSave}
								onCancel={baiCodeHandlers.handleCancel}
								onChange={baiCodeHandlers.handleChange}
								theme={theme}
								fieldLabelStyles={fieldLabelStyles}
								fieldValueStyles={fieldValueStyles}
								buttonStyles={buttonStyles}
							/>
						</Box>
					</Box>

					{/* Separator line */}
					<Box sx={{ 
						borderTop: `1px solid ${theme.palette.divider}`, 
						margin: '20px 0' 
					}} />

					{/* G/L Account and Custom Tags */}
					<Box sx={{ display: 'flex', gap: '40px' }}>
						{/* G/L Account */}
						<Box sx={{ flex: 1 }}>
							<Typography sx={fieldLabelStyles}>
								G/L ACCOUNT
							</Typography>
							<TagTransactionInput 
								record={props.selectedTransaction}
								onGLAccountChange={handleGLAccountChange}
								disabled={glAccountLoading}
								loading={glAccountLoading}
								showSubmitButton={true}
							/>
						</Box>

						{/* Custom Tags */}
						<Box sx={{ flex: 1 }}>
							{!props.loading && (
								<>
									<Typography sx={fieldLabelStyles}>
										CUSTOM TAGS
									</Typography>
									<SelectTransactionCategories
										customCategories={props.customCategories}
										defaultCategories={props.selectedTransaction.generic_tags}
										handleSelectCategories={handleSelectCategories}
										inFlight={inFlight}
										loading={inFlight}
										showSubmitButton={true}
									/>
								</>
							)}
						</Box>
					</Box>

					{/* Generic Tags Display */}
					{props.selectedTransaction?.counterparty?.generic_tags && (
						<Box sx={{ marginTop: '16px' }}>
							<GenericTagChips genericTags={props.selectedTransaction.counterparty.generic_tags}/>
						</Box>
					)}
				</Box>
			</Box>
		</LocalizationProvider>
	);
}

export default CompletedTransactionDrawer;